/**
 * Floating WhatsApp button.
 *
 * Keeps the button anchored to a screen corner while the page scrolls and, when the merchant
 * enables it, "releases" the button into the document flow near the footer so it settles
 * above it instead of floating over it.
 *
 * The theme's real scroll container is not always `window`: on desktop (>=990px) it is
 * `.page-wrapper`, and only on mobile does the document itself scroll (`html`/`body` have
 * `overflow-y: hidden` on desktop). All scroll reads and listeners go through
 * `@theme/scroll-container` instead of `window.scroll*`, which never fires here.
 */
import { getScrollContainer, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

const FLOATING_SELECTOR = '.floating-whatsapp-section';
const SECTION_ID_PREFIX = 'shopify-section-';

// This is the section's own responsive breakpoint for its settings (mobile vs desktop
// offsets), unrelated to the theme's 990px squeeze-layout breakpoint above.
const DESKTOP_SETTINGS_QUERY = window.matchMedia('(min-width: 750px)');

/**
 * Picks the mobile or desktop value of a setting based on the section's own breakpoint.
 *
 * @param {number} mobileValue
 * @param {number} desktopValue
 * @returns {number}
 */
function getCurrentOffset(mobileValue, desktopValue) {
  return DESKTOP_SETTINGS_QUERY.matches ? desktopValue : mobileValue;
}

/**
 * Falls back to `fallback` only when `parsedValue` is not a finite number. A plain
 * `parsedValue || fallback` would incorrectly discard an intentionally-configured `0`,
 * since `0` is falsy but a perfectly valid pixel value.
 *
 * @param {number} parsedValue - The result of `parseFloat` on a raw CSS custom property value.
 * @param {number} fallback
 * @returns {number}
 */
function withFallback(parsedValue, fallback) {
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

/**
 * Initializes the "release near footer" behavior for a single floating WhatsApp button.
 * Idempotent: tears down any previously attached listeners on the element before attaching
 * new ones, so it is safe to call again on `shopify:section:load`.
 *
 * @param {HTMLElement} floatingElement - The `.floating-whatsapp-section` element.
 */
function initFloatingWhatsapp(floatingElement) {
  if (typeof floatingElement._floatingWhatsappCleanup === 'function') {
    floatingElement._floatingWhatsappCleanup();
  }

  const enableRelease = floatingElement.dataset.enableRelease === 'true';

  if (!enableRelease) {
    floatingElement.style.position = '';
    floatingElement.style.top = '';
    floatingElement.style.right = '';
    floatingElement.style.bottom = '';
    floatingElement._floatingWhatsappCleanup = null;
    return;
  }

  // The section wrapper Shopify renders around this section's content. It is not
  // `position: fixed`, so it stays in normal document flow and its own bounding rect marks
  // where this section instance sits among the other footer sections. Walk up via `closest`
  // instead of assuming `floatingElement.parentElement` is the wrapper — correct today because
  // the schema's `tag: div` makes it so, but this survives any future wrapper change.
  const sectionElement = floatingElement.closest(`[id^="${SECTION_ID_PREFIX}"]`);

  if (!sectionElement) return;

  // These custom properties come from a Liquid-rendered `style=` attribute, so they cannot
  // change between section renders (a re-render fires `shopify:section:load`, which
  // re-initializes this function from scratch). Read them once here instead of on every
  // rAF-driven frame; `getCurrentOffset` below still re-picks mobile vs. desktop per frame,
  // since that depends on the live viewport width.
  const computedStyles = getComputedStyle(floatingElement);
  const bottomOffsetMobile = withFallback(
    parseFloat(computedStyles.getPropertyValue('--floating-whatsapp-offset-bottom-mobile')),
    16
  );
  const bottomOffsetDesktop = withFallback(
    parseFloat(computedStyles.getPropertyValue('--floating-whatsapp-offset-bottom-desktop')),
    24
  );
  const rightOffsetMobile = withFallback(
    parseFloat(computedStyles.getPropertyValue('--floating-whatsapp-offset-right-mobile')),
    16
  );
  const rightOffsetDesktop = withFallback(
    parseFloat(computedStyles.getPropertyValue('--floating-whatsapp-offset-right-desktop')),
    24
  );
  // Fallbacks match the schema's `release_spacing_mobile`/`release_spacing_desktop` defaults
  // (80/210), not the offset fallbacks above — release spacing and edge offsets are different
  // settings with different defaults.
  const releaseSpacingMobile = withFallback(
    parseFloat(computedStyles.getPropertyValue('--floating-whatsapp-release-spacing-mobile')),
    80
  );
  const releaseSpacingDesktop = withFallback(
    parseFloat(computedStyles.getPropertyValue('--floating-whatsapp-release-spacing-desktop')),
    210
  );

  const updatePosition = () => {
    const floatingHeight = floatingElement.offsetHeight;
    const containerHeight = getScrollContainer().clientHeight;

    if (!floatingHeight || !containerHeight) return;

    const bottomOffset = getCurrentOffset(bottomOffsetMobile, bottomOffsetDesktop);
    const rightOffset = getCurrentOffset(rightOffsetMobile, rightOffsetDesktop);
    const releaseSpacing = getCurrentOffset(releaseSpacingMobile, releaseSpacingDesktop);

    // `getBoundingClientRect()` is always viewport-relative, regardless of which element is
    // actually the scroll container, so this comparison holds in both desktop and mobile mode.
    const sectionRect = sectionElement.getBoundingClientRect();
    const shouldRelease = containerHeight - bottomOffset >= sectionRect.top - releaseSpacing;

    if (shouldRelease) {
      floatingElement.style.position = 'fixed';
      floatingElement.style.top = `${sectionRect.top - floatingHeight - releaseSpacing}px`;
      // Keep the safe-area term the stylesheet applies: writing a bare pixel value here would
      // drop it, putting the button inside the home indicator strip on notched devices.
      floatingElement.style.right = `calc(${rightOffset}px + env(safe-area-inset-right))`;
      floatingElement.style.bottom = 'auto';
    } else {
      floatingElement.style.position = '';
      floatingElement.style.top = '';
      floatingElement.style.right = '';
      floatingElement.style.bottom = '';
    }
  };

  const requestPositionUpdate = () => window.requestAnimationFrame(updatePosition);

  // `getScrollEventTarget()` returns `.page-wrapper` on desktop and `document` on mobile.
  // Keep a reference to whichever target we actually subscribed to, so cleanup (and
  // re-subscription below) always removes the listener from the right place.
  let scrollTarget = getScrollEventTarget();
  scrollTarget.addEventListener('scroll', requestPositionUpdate, { passive: true });
  window.addEventListener('resize', requestPositionUpdate);

  // A `position: fixed` released button has no built-in way to react to layout changes that
  // aren't a scroll or a resize (a web font swap, a lazy image resolving, a footer accordion
  // expanding). Neither the section wrapper nor `.shopify-section-group-footer-group` is a
  // usable observation target: the wrapper is zero-height and never resizes, and that class is
  // stamped on every section in the group (including this one's own wrapper) rather than on a
  // container, so `closest()` on it just finds the wrapper again. The `<footer>` element is the
  // actual container whose box grows when any section inside it grows — both sections declare
  // `enabled_on: { groups: ["footer"] }`, so it's always present; `parentElement` is only a
  // defensive fallback for a store whose layout doesn't wrap sections in `<footer>`.
  const positionContext = sectionElement.closest('footer') ?? sectionElement.parentElement;
  const resizeObserver = positionContext ? new ResizeObserver(requestPositionUpdate) : null;
  resizeObserver?.observe(positionContext);

  const onScrollContainerChange = () => {
    scrollTarget.removeEventListener('scroll', requestPositionUpdate);
    scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('scroll', requestPositionUpdate, { passive: true });
    requestPositionUpdate();
  };

  scrollContainerMediaQuery.addEventListener('change', onScrollContainerChange);

  floatingElement._floatingWhatsappCleanup = () => {
    scrollTarget.removeEventListener('scroll', requestPositionUpdate);
    window.removeEventListener('resize', requestPositionUpdate);
    scrollContainerMediaQuery.removeEventListener('change', onScrollContainerChange);
    resizeObserver?.disconnect();
  };

  requestPositionUpdate();
}

/**
 * Initializes every floating WhatsApp button currently present in the page.
 */
function initAllFloatingWhatsapp() {
  document.querySelectorAll(FLOATING_SELECTOR).forEach((element) => {
    if (element instanceof HTMLElement) {
      initFloatingWhatsapp(element);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllFloatingWhatsapp, { once: true });
} else {
  initAllFloatingWhatsapp();
}

document.addEventListener('shopify:section:load', (event) => {
  const sectionElement = document.getElementById(`${SECTION_ID_PREFIX}${event.detail.sectionId}`);
  const floatingElement = sectionElement?.querySelector(FLOATING_SELECTOR);

  if (floatingElement instanceof HTMLElement) {
    initFloatingWhatsapp(floatingElement);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  const sectionElement = document.getElementById(`${SECTION_ID_PREFIX}${event.detail.sectionId}`);
  const floatingElement = sectionElement?.querySelector(FLOATING_SELECTOR);

  if (floatingElement instanceof HTMLElement && typeof floatingElement._floatingWhatsappCleanup === 'function') {
    floatingElement._floatingWhatsappCleanup();
  }
});
